import { useState, type ReactNode } from 'react';
import type { Deliverable, DeliverableStatus } from '@pixerito/api-client';
import { formatDate, formatDay, hostOf, MODULE_LABEL, STATUS_LABEL, STATUS_ORDER } from '../lib/format';
import { Icon, StatusPill } from './ui';

function reachedAt(d: Deliverable, status: DeliverableStatus) {
  const change = [...d.history].reverse().find((h) => h.to === status);
  return change?.changedAt ?? (status === 'in_production' ? d.createdAt : null);
}

/** Linha do tempo: em produção → em revisão → entregue. */
export function StatusSteps({ deliverable }: { deliverable: Deliverable }) {
  const currentIndex = STATUS_ORDER.indexOf(deliverable.status);
  return (
    <ol className="steps" aria-label="Andamento">
      {STATUS_ORDER.map((s, i) => {
        const state = i < currentIndex ? 'done' : i === currentIndex ? 'current' : 'todo';
        const at = i <= currentIndex ? reachedAt(deliverable, s) : null;
        return (
          <li key={s} className={`steps__item steps__item--${state}`} aria-current={state === 'current' ? 'step' : undefined}>
            <span className="steps__dot" aria-hidden="true" />
            <span className="steps__label">{STATUS_LABEL[s]}</span>
            <span className="steps__date">{at ? formatDay(at) : ' '}</span>
          </li>
        );
      })}
    </ol>
  );
}

export function DeliverableCard({
  deliverable: d,
  actions,
  showModule = true,
}: {
  deliverable: Deliverable;
  actions?: ReactNode;
  showModule?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const older = d.versions.slice(1);

  return (
    <article className="card deliv">
      <div className="deliv__head">
        <div className="stack" style={{ gap: 6, minWidth: 0 }}>
          <div className="row" style={{ gap: 8 }}>
            <StatusPill status={d.status} />
            {showModule && <span className="tag">{MODULE_LABEL[d.module]}</span>}
            {d.dueDate && d.status !== 'delivered' && <span className="t-caption">Previsão: {formatDate(d.dueDate)}</span>}
          </div>
          <h3 className="t-h3">{d.title}</h3>
          {d.description && <p className="muted t-small">{d.description}</p>}
        </div>
        {actions && <div className="deliv__actions">{actions}</div>}
      </div>

      <StatusSteps deliverable={d} />

      <div className="deliv__file">
        {d.latestVersion ? (
          <>
            <div className="stack" style={{ gap: 2, minWidth: 0 }}>
              <span className="label">
                {d.status === 'delivered' ? 'Arquivo final' : 'Versão mais recente'} · v{d.latestVersion.number}
              </span>
              <span className="t-small muted deliv__note">
                {formatDate(d.latestVersion.createdAt)} · {hostOf(d.latestVersion.fileUrl)}
                {d.latestVersion.notes ? ` · ${d.latestVersion.notes}` : ''}
              </span>
            </div>
            <a
              className={`btn btn--sm ${d.status === 'delivered' ? 'btn--action' : 'btn--ghost'}`}
              href={d.latestVersion.fileUrl}
              target="_blank"
              rel="noreferrer"
            >
              Abrir arquivo <Icon name="arrowUpRight" size={15} />
            </a>
          </>
        ) : (
          <span className="t-small muted">Ainda sem arquivo. Ele aparece aqui quando a primeira versão ficar pronta.</span>
        )}
      </div>

      {older.length > 0 && (
        <div>
          <button className="btn btn--quiet btn--sm" style={{ paddingLeft: 0 }} onClick={() => setOpen((o) => !o)} aria-expanded={open}>
            {open ? 'Esconder' : 'Ver'} versões anteriores ({older.length})
          </button>
          {open && (
            <ul className="versions">
              {older.map((v) => (
                <li key={v.id}>
                  <span className="tag">v{v.number}</span>
                  <span className="t-small" style={{ minWidth: 0 }}>
                    {formatDate(v.createdAt)}
                    {v.notes ? ` · ${v.notes}` : ''}
                  </span>
                  <a href={v.fileUrl} target="_blank" rel="noreferrer" className="t-small hl">
                    abrir
                  </a>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </article>
  );
}
